import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { eur, num, showMoney } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const COLORS = ["hsl(var(--primary))", "#34d399", "#60a5fa", "#f472b6", "#fbbf24", "#a78bfa", "#f87171"];

const chartRows = (suppliers) => {
  const rows = {};
  suppliers.forEach((s) => s.points.forEach((p) => { (rows[p.date] ||= { date: p.date })[s.supplier] = p.price; }));
  return Object.values(rows).sort((a, b) => a.date.localeCompare(b.date));
};

const Change = ({ pct }) => (
  <span className={pct > 0 ? "text-rose-400" : pct < 0 ? "text-emerald-400" : "text-muted-foreground"}>{pct > 0 ? "+" : ""}{num(pct)}%</span>
);

export const PriceHistoryDialog = ({ type, open, onOpenChange }) => {
  const { data, isLoading } = useQuery({
    queryKey: ["price-history", type],
    queryFn: async () => (await api.get("/materials/price-history", { params: { fabric_type: type } })).data,
    enabled: open,
  });
  const sups = data?.suppliers || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="price-history-dialog">
        <DialogHeader>
          <DialogTitle>Storico prezzi · {type}</DialogTitle>
          <DialogDescription>Prezzo al metro di ogni fornitore nel tempo: quando hai inserito il tessuto e a ogni ordine fornitore.</DialogDescription>
        </DialogHeader>
        {isLoading && <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>}
        {!isLoading && sups.length === 0 && <p className="py-6 text-sm text-muted-foreground" data-testid="price-history-empty">Nessun prezzo registrato per questo tipo.</p>}
        {sups.length > 0 && (
          <>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={chartRows(sups)}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `€${v}`} width={48} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} formatter={(v, n) => [`${eur(v)}/m`, n]} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {sups.map((s, i) => <Line key={s.supplier} type="monotone" dataKey={s.supplier} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 3 }} connectNulls />)}
              </LineChart>
            </ResponsiveContainer>
            <div className="space-y-2">
              {sups.map((s, i) => (
                <div key={s.supplier} className="rounded-md border border-border p-3 text-sm" data-testid={`price-history-supplier-${i}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium">{s.supplier}{i === 0 && sups.length > 1 && <span className="ml-2 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] text-emerald-400">oggi il più conveniente</span>}</span>
                    <span>da {eur(s.first)} a <b>{eur(s.last)}</b>/m · <Change pct={s.change_pct} /></span>
                  </div>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {s.points.map((p) => `${p.date} ${eur(p.price)} (${p.source}${p.color ? `, ${p.color}` : ""})`).join(" · ")}
                  </p>
                </div>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
